import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import PaginatedResponse from "_shared/_api/pagination/responses/paginated-response";
import { RootState, StoreState } from "_shared/_stores/redux-store";
import Guid from "_shared/guids/guid";
import { messagingApi } from "messaging/_api/messaging-api";
import Message from "messaging/message";

const initialState: StoreState<PaginatedResponse<Message>> = {
  entries: {},
  loading: {},
  error: {},
};

const messagesSlice = createSlice({
  name: "messages",
  initialState,
  reducers: {
    addMessage: (state, action: PayloadAction<Message>) => {
      const forumId = action.payload.forum.id?.value;
      if (!forumId) return;

      let existingForumMessages = state.entries[forumId];

      if (!existingForumMessages) {
        existingForumMessages = PaginatedResponse.empty<Message>();
      }

      existingForumMessages = existingForumMessages.appendOrReplaceData([action.payload]);
      state.entries[forumId] = existingForumMessages;
    },
    removeMessage: (state, action: PayloadAction<Guid>) => {
      for (const [forumId, forumMessages] of Object.entries(state.entries)) {
        if (!forumMessages) continue;
        state.entries[forumId] = forumMessages.removeDataById(action.payload);
      }
    },
    clearMessages: (state, action: PayloadAction<Guid>) => {
      const forumId = action.payload.value;
      if (!forumId) return;

      state.entries[forumId] = PaginatedResponse.empty<Message>();
      state.loading[forumId] = false;
      state.error[forumId] = null;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(messagingApi.endpoints.getMessagesByForum.matchPending, (state, action) => {
      const forumId = action.meta.arg.originalArgs?.forumId;
      if (!forumId) return;

      state.loading[forumId] = true;
      state.error[forumId] = null;
    });
    builder.addMatcher(messagingApi.endpoints.getMessagesByForum.matchFulfilled, (state, action) => {
      const { forumId, append } = action.meta.arg.originalArgs;
      if (!forumId) return;

      let existingForumMessages = state.entries[forumId] ?? PaginatedResponse.empty<Message>();
      if (!existingForumMessages.isEmpty && append) {
        existingForumMessages = existingForumMessages.appendOrReplaceData(
          action.payload.data,
          action.payload.headers,
          (a, b) => (a.created?.diff(b.created) ?? 0) || (a.published?.diff(b.published) ?? 0)
        );
        state.entries[forumId] = existingForumMessages;
      } else {
        state.entries[forumId] = action.payload;
      }
      state.loading[forumId] = false;
    });
    builder.addMatcher(messagingApi.endpoints.getMessagesByForum.matchRejected, (state, action) => {
      const forumId = action.meta.arg.originalArgs?.forumId;
      if (!forumId) return;

      state.loading[forumId] = false;
      state.error[forumId] = action.error;
    });
    builder.addMatcher(messagingApi.endpoints.createMessage.matchFulfilled, (state, action) => {
      upsertMessage(state, action.payload);
    });
    builder.addMatcher(messagingApi.endpoints.createBulkMessages.matchFulfilled, (state, action) => {
      action.payload.forEach((message) => upsertMessage(state, message));
    });
    builder.addMatcher(messagingApi.endpoints.deleteMessage.matchFulfilled, (state, action) => {
      const messageId = new Guid(action.meta.arg.originalArgs.messageId);
      for (const [forumId, forumMessages] of Object.entries(state.entries)) {
        if (!forumMessages) continue;
        state.entries[forumId] = forumMessages.removeDataById(messageId);
      }
    });
  },
});

function upsertMessage(state: { entries: Record<string, unknown> }, message: Message) {
  const forumId = message.forum.id?.value;
  if (!forumId) return;

  const existingForumMessages =
    (state.entries[forumId] as PaginatedResponse<Message> | undefined) ?? PaginatedResponse.empty<Message>();
  state.entries[forumId] = existingForumMessages.appendOrReplaceData([message]);
}

const selectEntries = (state: RootState) => state.messages.entries;

const selectMessagesByForumId = createSelector(
  [selectEntries, (_: RootState, forumId?: Guid) => forumId?.value ?? ""],
  (entries, id) => entries[id] ?? []
);

export const { addMessage, removeMessage, clearMessages } = messagesSlice.actions;

export const getIsLoadingMessagesByForumId = (forumId?: Guid) => (state: RootState) => {
  if (!forumId) return false;
  return state.messages?.loading[forumId.value];
};
export const getMessagesByForumId = (forumId?: Guid) => (state: RootState) => selectMessagesByForumId(state, forumId);

export const getErrorLoadingMessagesByForumId = (forumId?: Guid) => (state: RootState) =>
  state.messages.error[forumId?.value ?? ""];

export const getMessageHasImagesAttached = (message: Message) => {
  return message.attachments.some((attachment) => attachment.isImage);
};

export default messagesSlice;
